import React, { useState, useMemo } from 'react';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import { useAppContext } from '../contexts/AppContext';
import { generateFmeaDrafts, generatePDiagram } from '../services/geminiService';
import { FmeaItem, PDiagramData } from '../types';
import { Sparkles, Save, CheckSquare, Square, FileText, AlertTriangle, RefreshCw } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import { calculateActionPriority, calculateRPN } from '../utils/riskCalculators';
import { getStandardConfig } from '../constants/fmeaStandards';

const FmeaGeneratorPage: React.FC = () => {
    const { allData, updateFmeaData, refreshData, masterFunctions, navigate, addEpoch, addPDiagram, activeProject } = useAppContext();
    const [selectedDiagramId, setSelectedDiagramId] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<{ markdownReport: string, newFmeaItems: Partial<FmeaItem>[] } | null>(null);
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

    // --- Magnus Genesis & Evolution Logic ---
    const [mode, setMode] = useState<'default' | 'genesis'>('default');
    const [genesisType, setGenesisType] = useState<'fresh' | 'evolution' | 'batch'>('fresh');
    const [baseEpochId, setBaseEpochId] = useState<number | null>(null);
    const [selectedReqIds, setSelectedReqIds] = useState<Set<string>>(new Set());

    const [genesisStep, setGenesisStep] = useState<number>(0);
    const [genesisLogs, setGenesisLogs] = useState<string[]>([]);
    const [isGenesisRunning, setIsGenesisRunning] = useState(false);
    const [generatedDesign, setGeneratedDesign] = useState<PDiagramData | null>(null);

    const pDiagramsAll = allData.pDiagramList || [];
    const availableEpochs = allData.iterationHistory || [];
    const customerRequirements = allData.customerRequirements || [];

    // **PROJECT FILTERING for P-Diagrams**
    const isItemInProject = (diagram: PDiagramData) => {
        if (!activeProject || activeProject === 'all') return true;
        const activeProjectNormalized = normalizeProjectName(activeProject);
        const diagramProjectNormalized = normalizeProjectName(diagram.projectName || '');

        if (diagramProjectNormalized) {
            return diagramProjectNormalized === activeProjectNormalized;
        }

        if (diagram.epochId) {
            const epoch = allData.iterationHistory?.find(e =>
                (e.epochNumber || e.iteration) === diagram.epochId
            );
            if (epoch) {
                const projectName = extractProjectName(epoch.epochName || '');
                return normalizeProjectName(projectName) === activeProjectNormalized;
            }
        }

        if (!diagram.projectName && !diagram.epochId) {
            return activeProjectNormalized === 'aerator pro';
        }

        return false;
    };

    const pDiagrams = pDiagramsAll.filter(d => isItemInProject(d));

    // Extract unique Projects for metadata assignment
    const localProjects = useMemo(() => {
        const projectMap = new Map<string, Set<number>>();
        if (allData.iterationHistory) {
            allData.iterationHistory.forEach((ep) => {
                let projectName = "Other";
                if (ep.epochName) {
                    if (ep.epochName.includes(' - ')) {
                        projectName = ep.epochName.split(' - ')[0].trim();
                    } else if (ep.epochName.startsWith('Magnus Genesis')) {
                        projectName = "Magnus Genesis";
                    } else {
                        projectName = ep.epochName;
                    }
                }
                if (!projectMap.has(projectName)) projectMap.set(projectName, new Set());
                projectMap.get(projectName)!.add(ep.epochNumber || ep.iteration || 0);
            });
        }
        return Array.from(projectMap.entries()).map(([name, epochs]) => ({
            name,
            epochIds: Array.from(epochs).sort((a, b) => a - b)
        }));
    }, [allData.iterationHistory]);

    const handleGenerate = async () => {
        const diagram = pDiagrams.find(d => d.consolidatedFunctionId === selectedDiagramId);
        if (!diagram) return;

        setIsLoading(true);
        try {
            const existingFmea = allData.fmeaItems.filter(f => f.function === diagram.consolidatedFunctionId);

            // Calculate available top-level functions to help AI match
            const allFunctions = Array.from(new Set([...masterFunctions, ...allData.pDiagramList.map(p => p.consolidatedFunctionId)]));
            const fmeaStandard = allData.projectMetadata?.fmeaStandard || 'AIAG_VDA';

            const data = await generateFmeaDrafts(diagram, existingFmea, allData.controls, allData.dvprTests, allFunctions, [], 20, [], {
                projectName: activeProject !== 'all' ? activeProject : undefined,
                fmeaStandard
            } as any);
            setResult(data);
            // Select all by default
            setSelectedIndices(new Set(data.newFmeaItems.map((_, i) => i)));
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : String(error);
            alert(`Failed to generate FMEA items: ${message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSelection = (index: number) => {
        const newSet = new Set(selectedIndices);
        if (newSet.has(index)) newSet.delete(index);
        else newSet.add(index);
        setSelectedIndices(newSet);
    };

    const toggleReqSelection = (id: string) => {
        const newSet = new Set(selectedReqIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedReqIds(newSet);
    };

    const handleSave = async () => {
        if (!result) return;

        const diagram = pDiagrams.find(d => d.consolidatedFunctionId === selectedDiagramId) || pDiagrams[0] || { consolidatedFunctionId: 'system-genesis', systemName: 'Aerator System', goal: [], inputSignals: [], controlFactors: [], systemFunction: [], expectedOutput: [], unexpectedOutput: [], noiseFactors: { pieceToPiece: [], changeOverTime: [], customerUsage: [], externalEnvironment: [], internal: [] } }; // Fallback for genesis mode

        let epochId: number | undefined;
        let projectName: string | undefined;

        if (activeProject && activeProject !== 'all') {
            projectName = activeProject;
            const normalizedActive = normalizeProjectName(activeProject);
            const proj = localProjects.find(p => normalizeProjectName(p.name) === normalizedActive);
            if (proj && proj.epochIds.length > 0) {
                epochId = proj.epochIds[proj.epochIds.length - 1];
            }
        }

        const itemsToAdd = result.newFmeaItems
            .filter((_, i) => selectedIndices.has(i))
            .map(item => ({
                ...item,
                id: `FMEA - GEN - ${Date.now()} -${Math.random().toString(36).substr(2, 5)} `,
                // Use the function returned by AI, or fallback to diagram ID
                function: item.function || diagram.consolidatedFunctionId,
                status: 'draft',
                RPN: calculateRPN(item.S || 1, item.O || 1, item.D || 1),
                actionPriority: calculateActionPriority(item.S || 1, item.O || 1, item.D || 1),
                preventionControls: item.preventionControls || [],
                detectionControls: item.detectionControls || [],
                notes: item.notes || '',
                projectName,
                epochId
            } as FmeaItem));

        if (itemsToAdd.length === 0) return;

        try {
            await updateFmeaData([...allData.fmeaItems, ...itemsToAdd]);
            alert(`${itemsToAdd.length} items saved as Drafts.`);
            navigate('/fmeas', { state: { view: 'draft' } }); // Pass state to navigate to drafts
        } catch (error) {
            console.error("Error saving items", error);
            alert("Error saving items.");
        }
    };

    // ==================== REFACTORED runGenesis HELPERS ====================
    // These helper functions reduce cyclomatic complexity while preserving functionality

    const addGenesisLog = (msg: string) => setGenesisLogs(prev => [...prev, msg]);

    const runFreshGenesis = async () => {
        addGenesisLog("🤖 [Mag-Functional]: Analyzing system architecture (Fresh Start)...");
        await new Promise(r => setTimeout(r, 1000));
        addGenesisLog("🤖 [Mag-Functional]: Identifying primary functions and interactions...");
        await new Promise(r => setTimeout(r, 1000));
        setGenesisStep(1);

        addGenesisLog("⚙️ [Mag-Physics]: Applying physical boundary conditions...");
        await new Promise(r => setTimeout(r, 1200));
        addGenesisLog("⚙️ [Mag-Physics]: Establishing noise factor profiles...");
        await new Promise(r => setTimeout(r, 1000));
        setGenesisStep(2);

        addGenesisLog("🧠 [Magnus-Core]: Generative AI creating optimal P-Diagram structure...");

        try {
            const genesisDesign = await generatePDiagram(
                "High-Efficiency Fluid Aerator System. Primary function: Aerate fluid to 20% dissolved oxygen. Key components: High-speed Motor (24V DC), Impeller, Housing, Bearings, Seals. Constraints: Max 15,000 RPM, Temp < 80C. Environment: High humidity."
            );

            genesisDesign.consolidatedFunctionId = 'genesis-aerator-v1';
            genesisDesign.systemName = 'Aerator Concept (Genesis AI)';

            setGeneratedDesign(genesisDesign);
            addGenesisLog("✨ [Magnus-Core]: P-Diagram generated successfully.");

            addGenesisLog("🧠 [Magnus-Core]: Generating FMEA candidates (Batch Limit: 20)...");
            const allFunctions = Array.from(new Set([...masterFunctions, ...allData.pDiagramList.map(p => p.consolidatedFunctionId)]));
            const existingFmea = allData.fmeaItems;
            const fmeaStandard = allData.projectMetadata?.fmeaStandard || 'AIAG_VDA';

            const data = await generateFmeaDrafts(genesisDesign, existingFmea, allData.controls, allData.dvprTests, allFunctions, [], 20, [], {
                projectName: activeProject !== 'all' ? activeProject : undefined,
                fmeaStandard
            } as any);

            setResult(data);
            setSelectedIndices(new Set(data.newFmeaItems.map((_, i) => i)));
            addGenesisLog("✅ [Magnus-Core]: Genesis complete. Items ready.");
            setGenesisStep(3);
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e);
            addGenesisLog("❌ [Error]: " + message);
            console.error(e);
        } finally {
            setIsGenesisRunning(false);
        }
    };

    const runBatchGenesis = async () => {
        addGenesisLog("🚀 [Batch]: Initiating 7-Epoch Autonomous Simulation...");
        await new Promise(r => setTimeout(r, 800));

        const focusedReqs = customerRequirements.filter(r => selectedReqIds.has(r.id));
        const reqsToUse = focusedReqs.length > 0 ? focusedReqs : customerRequirements;

        addGenesisLog(`📝 [Config]: Generating based on ${reqsToUse.length} requirements.`);
        if (reqsToUse.length === 0) {
            addGenesisLog("⚠️ [Warning]: No requirements found. Using defaults.");
        }

        const payload = {
            product_category: reqsToUse.length > 0 ? reqsToUse[0].requirementText.split(' ').slice(0, 3).join(' ') : "Industrial Pump",
            requirements: reqsToUse.map(r => ({ requirementText: r.requirementText }))
        };

        addGenesisLog(`📡 [Backend]: Sending job to inference engine (Product: "${payload.product_category}")...`);

        try {
            const result = await authenticatedFetchJSON('/api/epochs/generate-seven-real', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            addGenesisLog(`✅ [Backend]: Job started! ID: ${result.status} (Message: ${result.message})`);

            setGenesisStep(1);
            addGenesisLog("⏳ [Monitor]: Polling generation status...");

            let attempts = 0;
            const maxAttempts = 60;

            const pollInterval = setInterval(async () => {
                attempts++;
                try {
                    const responseJson = await authenticatedFetchJSON('/api/epochs/status');

                    if (responseJson.success && responseJson.status) {
                        const statusData = responseJson.status;

                        if (statusData.is_running) {
                            if (attempts % 2 === 0) {
                                addGenesisLog(`🔄 [Generating]: Currently on Epoch ${statusData.current_epoch}/7...`);
                            }
                        } else {
                            if (statusData.completed_count >= 7 || statusData.current_epoch >= 7) {
                                clearInterval(pollInterval);
                                addGenesisLog("✅ [Complete]: 7-Epoch Generation Success!");
                                setGenesisStep(3);
                                setIsGenesisRunning(false);
                                alert("Batch Generation Complete! \n\nPlease go to 'Epoch Manager' and click 'Import Generated' to view results.");
                            } else if (statusData.error) {
                                clearInterval(pollInterval);
                                addGenesisLog(`❌ [Error]: Generation failed: ${statusData.error}`);
                                setIsGenesisRunning(false);
                            }
                        }
                    }

                    if (attempts >= maxAttempts) {
                        clearInterval(pollInterval);
                        addGenesisLog("⚠️ [Timeout]: Polling timed out. Check backend logs.");
                        setIsGenesisRunning(false);
                    }
                } catch (e) {
                    console.error("Polling error", e);
                }
            }, 3000);

            return; // Let interval run
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e);
            addGenesisLog(`❌ [Error]: ${message}`);
            setIsGenesisRunning(false);
        }
    };

    const runEvolutionGenesis = async () => {
        if (baseEpochId === null) {
            addGenesisLog("❌ [Error]: No Base Epoch Selected.");
            setIsGenesisRunning(false);
            return;
        }

        addGenesisLog(`🧬 [Evolution]: Loading context from Epoch ${baseEpochId}...`);
        await new Promise(r => setTimeout(r, 1000));

        const existingFmea = allData.fmeaItems;
        const focusedReqs = customerRequirements.filter(r => selectedReqIds.has(r.id));
        addGenesisLog(`🎯 [Focus]: Targeting ${focusedReqs.length > 0 ? focusedReqs.length : 'ALL'} Customer Requirements.`);

        setGenesisStep(1);

        const latestDesign = pDiagrams.length > 0 ? pDiagrams[pDiagrams.length - 1] : null;
        if (!latestDesign) {
            addGenesisLog("❌ [Error]: No P-Diagram found to evolve.");
            setIsGenesisRunning(false);
            return;
        }

        addGenesisLog("⚙️ [Mag-Physics]: Analyzing Requirement Gaps & Physics Constraints...");
        await new Promise(r => setTimeout(r, 1500));
        setGenesisStep(2);

        try {
            addGenesisLog("🧠 [Magnus-Core]: Generating Evolutionary FMEA candidates (Max 20)...");
            const allFunctions = Array.from(new Set([...masterFunctions, ...allData.pDiagramList.map(p => p.consolidatedFunctionId)]));
            const fmeaStandard = allData.projectMetadata?.fmeaStandard || 'AIAG_VDA';

            const data = await generateFmeaDrafts(latestDesign, existingFmea, allData.controls, allData.dvprTests, allFunctions, focusedReqs, 20, [], {
                projectName: activeProject !== 'all' ? activeProject : undefined,
                fmeaStandard
            } as any);

            setResult(data);
            setSelectedIndices(new Set(data.newFmeaItems.map((_, i) => i)));
            addGenesisLog("✅ [Magnus-Core]: Evolution complete. Review candidates.");
            setGenesisStep(3);
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e);
            addGenesisLog("❌ [Error]: " + message);
        } finally {
            setIsGenesisRunning(false);
        }
    };

    // ==================== MAIN runGenesis FUNCTION ====================
    // Refactored to reduce cyclomatic complexity from 80 to ~5
    const runGenesis = async () => {
        setIsGenesisRunning(true);
        setGenesisStep(0);
        setGenesisLogs([]);
        setResult(null);
        setGeneratedDesign(null);

        switch (genesisType) {
            case 'fresh':
                await runFreshGenesis();
                break;
            case 'batch':
                await runBatchGenesis();
                break;
            case 'evolution':
                await runEvolutionGenesis();
                break;
        }
    };

    const saveGenesisEpoch = async () => {
        if (!result) return;

        // 1. Save Design if Fresh
        if (genesisType === 'fresh' && generatedDesign && addPDiagram) {
            await addPDiagram(generatedDesign);
        }

        // 2. Determine New Epoch ID
        // If Fresh, ID = 1. If Evolution, ID = Base + 1 (or max + 1)
        const nextEpochId = genesisType === 'fresh' ? 1 : (baseEpochId ? baseEpochId + 1 : (Math.max(...availableEpochs.map(e => e.epochNumber), 0) + 1));

        // 3. Save Items
        setSelectedIndices(new Set(result.newFmeaItems.map((_, i) => i)));

        const newItems = result.newFmeaItems.map(item => ({
            ...item,
            id: `FMEA-${genesisType === 'fresh' ? 'GEN' : 'EVO'}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            function: item.function || 'System',
            status: 'draft',
            RPN: calculateRPN(item.S || 1, item.O || 1, item.D || 1),
            actionPriority: calculateActionPriority(item.S || 1, item.O || 1, item.D || 1),
            preventionControls: item.preventionControls || [],
            detectionControls: item.detectionControls || [],
            notes: item.notes || '',
        } as FmeaItem));
        // 3. Add FMEA Items with Project Metadata
        const projectName = allData.projectMetadata?.name || 'Magnus Genesis';
        const itemsWithMetadata = newItems.map(item => ({
            ...item,
            epochId: nextEpochId,
            projectName: projectName,
            status: 'active' as const
        }));

        if (newItems.length > 0) {
            try {
                await updateFmeaData([...allData.fmeaItems, ...itemsWithMetadata]);
            } catch (e) {
                console.error("Error saving items", e);
            }
        }

        // 4. Create Epoch Record with Project Name
        const newEpoch = {
            iteration: nextEpochId,
            epochNumber: nextEpochId,
            epochName: (() => {
                if (genesisType === 'fresh') return `${projectName} - Epoch 1`;

                // Try to preserve project name from base epoch
                if (baseEpochId) {
                    const baseEpoch = availableEpochs.find(e => e.epochNumber === baseEpochId);
                    if (baseEpoch && baseEpoch.epochName) {
                        if (baseEpoch.epochName.includes(' - ')) {
                            const baseProjName = baseEpoch.epochName.split(' - ')[0].trim();
                            return `${baseProjName} - Evolutionary Epoch ${nextEpochId}`;
                        } else if (baseEpoch.epochName.startsWith('Magnus Genesis')) {
                            return `Magnus Genesis - Evolutionary Epoch ${nextEpochId}`;
                        }
                    }
                }

                return `${projectName} - Evolutionary Epoch ${nextEpochId}`;
            })(),
            iterationDate: new Date().toISOString().split('T')[0],
            phase: genesisType === 'fresh' ? 'Concept' : 'Design',
            status: 'completed',
            projectName: projectName, // Add project name to epoch
            totalRPN: newItems.reduce((acc, item) => acc + ((item.S || 1) * (item.O || 1) * (item.D || 1)), 0),
            avgRPN: Math.round(newItems.reduce((acc, item) => acc + ((item.S || 1) * (item.O || 1) * (item.D || 1)), 0) / (newItems.length || 1)),
            totalCost: 0,
            notes: genesisType === 'fresh' ? "Initial Genesis" : `Evolution from Epoch ${baseEpochId}. Focus: ${selectedReqIds.size} Reqs.`,
            changes: []
        };

        if (addEpoch) {
            await addEpoch(newEpoch);
            alert(`✅ Epoch ${nextEpochId} Saved! Added ${newItems.length} items.`);
        }
    }

    const standardConfig = useMemo(() => {
        return getStandardConfig(allData.projectMetadata?.fmeaStandard);
    }, [allData.projectMetadata?.fmeaStandard]);

    const headerLabels = useMemo(() => {
        const labels: Record<string, string> = {
            failureMode: 'Failure Mode',
            effects: 'Effect'
        };
        standardConfig.columns.forEach(c => {
            if (c.key === 'failureMode') labels.failureMode = c.header;
            if (c.key === 'effects') labels.effects = c.header;
        });
        return labels;
    }, [standardConfig]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                        <Sparkles className="text-primary" /> FMEA Ideation
                    </h1>
                    <p className="text-text-secondary mt-1">Generate FMEA drafts from P-Diagrams using AI.</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setMode(mode === 'default' ? 'genesis' : 'default')}
                        className={`px - 4 py - 2 rounded font - medium transition - colors ${mode === 'genesis' ? 'bg-purple-600 text-white shadow-lg' : 'bg-secondary/10 text-secondary hover:bg-secondary/20'} `}
                    >
                        {mode === 'genesis' ? 'Exit Genesis Mode' : '🚀 Try Magnus Genesis'}
                    </button>
                    <ModuleInfoModal
                        title="AI FMEA Ideation"
                        description="Leverage generative AI to identify potential failure modes based on your P-Diagrams."
                        benefits={[
                            "Overcome writer's block during brainstorming.",
                            "Capture subtle failure modes often missed by humans.",
                            "Auto-populate S/O/D ratings based on historical data."
                        ]}
                        usageInstructions={[
                            "Select a P-Diagram source.",
                            "Click 'Generate Analysis'.",
                            "Review proposed items and check the box to 'Save as Draft'."
                        ]}
                    />
                </div>
            </div>

            {mode === 'genesis' ? (
                <div className="bg-slate-900 border border-purple-500/30 rounded-lg p-6 text-slate-100 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
                    <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4">
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent flex items-center gap-2">
                            <RefreshCw className={isGenesisRunning ? "animate-spin" : ""} /> Magnus Genesis Algorithm
                        </h2>
                        <div className="flex gap-2">
                            <span className={`px - 2 py - 1 rounded text - xs border ${genesisStep >= 1 ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-white/20 text-white/40'} `}>Functional Agent</span>
                            <span className={`px - 2 py - 1 rounded text - xs border ${genesisStep >= 2 ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'border-white/20 text-white/40'} `}>Physics Agent</span>
                            <span className={`px - 2 py - 1 rounded text - xs border ${genesisStep >= 3 ? 'bg-purple-500/20 border-purple-500 text-purple-400' : 'border-white/20 text-white/40'} `}>Synthesis</span>
                        </div>
                    </div>

                    {/* Setup Panel */}
                    {!isGenesisRunning && !result && (
                        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-800/50 rounded border border-white/5">
                            <div>
                                <h3 className="text-sm font-semibold text-purple-300 uppercase mb-3">1. Genesis Mode</h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setGenesisType('fresh')}
                                        className={`flex-1 py-3 px-4 rounded border text-sm font-medium transition-all ${genesisType === 'fresh' ? 'bg-purple-500 text-white border-purple-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                                    >
                                        ✨ Fresh Start (Epoch 1)
                                    </button>
                                    <button
                                        onClick={() => setGenesisType('evolution')}
                                        className={`flex-1 py-3 px-4 rounded border text-sm font-medium transition-all ${genesisType === 'evolution' ? 'bg-blue-500 text-white border-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                                    >
                                        🧬 Evolution (Next Epoch)
                                    </button>
                                    <button
                                        onClick={() => setGenesisType('batch')}
                                        className={`flex-1 py-3 px-4 rounded border text-sm font-medium transition-all ${genesisType === 'batch' ? 'bg-green-600 text-white border-green-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                                    >
                                        🚀 Batch (7 Epochs)
                                    </button>
                                </div>

                                {genesisType === 'batch' && (
                                    <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded animate-in fade-in slide-in-from-top-2">
                                        <p className="text-xs text-green-300 mb-2">
                                            <strong>Batch Simulation Mode:</strong> This will run a full 7-epoch simulation on the backend (approx 1-3 mins).
                                        </p>
                                        <p className="text-xs text-slate-400">
                                            The system will autonomously generate: Concept → Design → Prototype → Validation → Production phases.
                                        </p>
                                    </div>
                                )}

                                {genesisType === 'evolution' && (
                                    <div className="mt-4 animate-in fade-in slide-in-from-top-2">
                                        <label className="block text-xs font-semibold text-slate-400 mb-2">Continue from Epoch:</label>
                                        <select
                                            className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-sm"
                                            value={baseEpochId || ''}
                                            onChange={e => setBaseEpochId(Number(e.target.value))}
                                        >
                                            <option value="">-- Select Base Epoch --</option>
                                            {availableEpochs.map(e => (
                                                <option key={e.epochNumber} value={e.epochNumber}>
                                                    Epoch {e.epochNumber}: {e.epochName} ({new Date(e.iterationDate).toLocaleDateString()})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div>
                                <h3 className="text-sm font-semibold text-blue-300 uppercase mb-3">
                                    2. Focus Requirements {selectedReqIds.size > 0 && <span className="text-xs normal-case text-white/60">({selectedReqIds.size} selected)</span>}
                                </h3>
                                <div className="bg-slate-900 border border-slate-700 rounded h-[140px] overflow-y-auto p-2">
                                    {customerRequirements.length === 0 ? (
                                        <div className="text-xs text-slate-500 p-2 text-center">No Customer requirements found.</div>
                                    ) : (
                                        customerRequirements.map(req => (
                                            <div
                                                key={req.id}
                                                onClick={() => toggleReqSelection(req.id)}
                                                className={`flex items - start gap - 2 p - 2 rounded cursor - pointer mb - 1 transition - colors ${selectedReqIds.has(req.id) ? 'bg-blue-500/20 border border-blue-500/50' : 'hover:bg-slate-800 border border-transparent'} `}
                                            >
                                                <div className={`mt - 0.5 w - 4 h - 4 rounded border flex items - center justify - center ${selectedReqIds.has(req.id) ? 'bg-blue-500 border-blue-500' : 'border-slate-600'} `}>
                                                    {selectedReqIds.has(req.id) && <CheckSquare size={10} className="text-white" />}
                                                </div>
                                                <div className="text-xs text-slate-300">
                                                    <span className="font-bold text-blue-200">{req.id}:</span> {req.requirementText}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="mt-2 flex justify-end">
                                    <button
                                        onClick={() => setSelectedReqIds(selectedReqIds.size === customerRequirements.length ? new Set() : new Set(customerRequirements.map(r => r.id)))}
                                        className="text-xs text-slate-400 hover:text-white underline"
                                    >
                                        {selectedReqIds.size === customerRequirements.length ? 'Clear All' : 'Select All'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!result && !isGenesisRunning && genesisLogs.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-slate-400 mb-6">Initiate the multi-agent genesis loop to create Epoch 1 baseline.</p>
                            <button
                                onClick={runGenesis}
                                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-full font-bold text-white shadow-lg transform hover:scale-105 transition-all"
                            >
                                Start Genesis Sequence
                            </button>
                        </div>
                    )}

                    <div className="space-y-2 font-mono text-sm max-h-[300px] overflow-y-auto mb-6">
                        {genesisLogs.map((log, i) => (
                            <div key={i} className="animate-in slide-in-from-left-2 fade-in duration-300">
                                {log}
                            </div>
                        ))}
                        {isGenesisRunning && <div className="text-purple-400 animate-pulse">_</div>}
                    </div>

                    {result && (
                        <div className="border-t border-white/10 pt-6 animate-in slide-in-from-bottom-4 fade-in duration-500">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-white">Generated Baseline (Ready for Epoch 1)</h3>
                                <button
                                    onClick={saveGenesisEpoch}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded font-bold shadow-lg flex items-center gap-2"
                                >
                                    <Save size={16} /> Save as Epoch 1
                                </button>
                            </div>
                            <div className="bg-slate-800 rounded p-4 max-h-[400px] overflow-y-auto">
                                <table className="w-full text-left">
                                    <thead className="text-xs uppercase text-slate-500 border-b border-slate-700">
                                        <tr>
                                            <th className="pb-2">{headerLabels.failureMode}</th>
                                            <th className="pb-2">{headerLabels.effects}</th>
                                            <th className="pb-2">S</th>
                                            <th className="pb-2">O</th>
                                            <th className="pb-2">D</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-slate-300 text-sm">
                                        {result.newFmeaItems.map((item, i) => (
                                            <tr key={i} className="border-b border-slate-700/50">
                                                <td className="py-2 pr-2">{item.failureMode}</td>
                                                <td className="py-2 pr-2 text-xs text-slate-400">{item.effects}</td>
                                                <td className="py-2"><span className="text-red-400 font-bold">{item.S}</span></td>
                                                <td className="py-2"><span className="text-yellow-400">{item.O}</span></td>
                                                <td className="py-2"><span className="text-blue-400">{item.D}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                </div>
            ) : (
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <label className="block text-sm font-medium mb-2">Select P-Diagram Source</label>
                    <div className="flex gap-4">
                        <select
                            value={selectedDiagramId}
                            onChange={(e) => setSelectedDiagramId(e.target.value)}
                            className="flex-1 bg-background border border-border rounded p-2"
                        >
                            <option value="">-- Select a Function --</option>
                            {pDiagrams.map(d => (
                                <option key={d.consolidatedFunctionId} value={d.consolidatedFunctionId}>
                                    {d.systemName || d.consolidatedFunctionId}
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={refreshData}
                            title="Sync P-Diagrams from DB"
                            className="bg-secondary text-white px-3 py-2 rounded hover:bg-secondary/90"
                        >
                            <RefreshCw size={20} />
                        </button>

                        <button
                            onClick={handleGenerate}
                            disabled={!selectedDiagramId || isLoading}
                            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50"
                        >
                            {isLoading ? 'Generating...' : 'Generate Analysis'}
                        </button>
                    </div>
                </div>
            )}

            {
                result && mode === 'default' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-card border border-border rounded-lg p-6 shadow-sm h-fit">
                            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <FileText size={20} /> Analysis Report
                            </h2>
                            <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">
                                {result.markdownReport}
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <AlertTriangle size={20} /> Proposed Items ({result.newFmeaItems.length})
                                </h2>
                                <button
                                    onClick={handleSave}
                                    disabled={selectedIndices.size === 0}
                                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 text-sm disabled:opacity-50"
                                >
                                    <Save size={16} /> Save Selected as Drafts
                                </button>
                            </div>

                            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                                {result.newFmeaItems.map((item, index) => (
                                    <div
                                        key={index}
                                        onClick={() => toggleSelection(index)}
                                        className={`p - 3 border rounded cursor - pointer transition - colors ${selectedIndices.has(index)
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:bg-background/50'
                                            } `}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-1 text-primary">
                                                {selectedIndices.has(index) ? <CheckSquare size={18} /> : <Square size={18} />}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-sm">{item.failureMode}</h3>
                                                <p className="text-xs font-medium text-primary mt-1">Function: {item.function || selectedDiagramId}</p>
                                                <p className="text-xs text-text-secondary mt-1">{headerLabels.effects}: {item.effects}</p>
                                                {item.notes && (
                                                    <p className="text-xs text-text-secondary mt-1 italic border-l-2 border-primary/50 pl-2">
                                                        Note: {item.notes.length > 100 ? item.notes.substring(0, 100) + '...' : item.notes}
                                                    </p>
                                                )}
                                                <div className="flex gap-2 mt-2 text-xs">
                                                    <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded">S: {item.S}</span>
                                                    <span className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">O: {item.O}</span>
                                                    <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">D: {item.D}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default FmeaGeneratorPage;
